import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, FileText, File, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Export Dropdown Button Component
 * Provides PDF and Word export options for AI intelligence reports
 * 
 * @param {string} token - Auth token
 * @param {string} variant - Button variant (default, outline, ghost)
 * @param {string} size - Button size (default, sm, lg)
 * @param {string} reportType - Specific report type to export (optional, shows dropdown if not set)
 */
export function AIExportDropdown({ token, variant = "outline", size = "sm", reportType = null }) {
  const [downloading, setDownloading] = useState(null);

  const downloadFile = async (type, format) => {
    setDownloading(`${type}-${format}`);
    try {
      const endpoint = `/api/ai-intelligence/export/${type}/${format}`;
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const extensions = { pdf: 'pdf', word: 'docx' };
        const filename = `ai_${type}_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();

        toast.success(`Downloaded ${filename}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Export failed');
      }
    } catch (error) {
      toast.error('Error downloading file');
    } finally {
      setDownloading(null);
    }
  };

  // If specific reportType is provided, show simpler PDF/Word options
  if (reportType) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size={size} data-testid={`export-ai-${reportType}-btn`}>
            {downloading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Export
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem 
            onClick={() => downloadFile(reportType, 'pdf')}
            disabled={downloading === `${reportType}-pdf`}
            className="cursor-pointer"
          >
            <FileText className="h-4 w-4 mr-2 text-red-600" />
            Export as PDF
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => downloadFile(reportType, 'word')}
            disabled={downloading === `${reportType}-word`}
            className="cursor-pointer"
          >
            <File className="h-4 w-4 mr-2 text-blue-600" />
            Export as Word
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Full dropdown with all AI report types
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} data-testid="export-ai-btn">
          {downloading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export AI Reports
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Executive Brief</DropdownMenuLabel>
        <DropdownMenuItem 
          onClick={() => downloadFile('executive_brief', 'pdf')}
          disabled={downloading?.startsWith('executive_brief')}
          className="cursor-pointer"
        >
          <FileText className="h-4 w-4 mr-2 text-red-600" />
          PDF
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => downloadFile('executive_brief', 'word')}
          disabled={downloading?.startsWith('executive_brief')}
          className="cursor-pointer"
        >
          <File className="h-4 w-4 mr-2 text-blue-600" />
          Word
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        <DropdownMenuLabel>AI Insights</DropdownMenuLabel>
        <DropdownMenuItem 
          onClick={() => downloadFile('insights', 'pdf')}
          disabled={downloading?.startsWith('insights')}
          className="cursor-pointer"
        >
          <FileText className="h-4 w-4 mr-2 text-red-600" />
          PDF
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => downloadFile('insights', 'word')}
          disabled={downloading?.startsWith('insights')}
          className="cursor-pointer"
        >
          <File className="h-4 w-4 mr-2 text-blue-600" />
          Word
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Action Items</DropdownMenuLabel>
        <DropdownMenuItem 
          onClick={() => downloadFile('action_items', 'pdf')}
          disabled={downloading?.startsWith('action_items')}
          className="cursor-pointer"
        >
          <FileText className="h-4 w-4 mr-2 text-red-600" />
          PDF
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => downloadFile('action_items', 'word')}
          disabled={downloading?.startsWith('action_items')}
          className="cursor-pointer"
        >
          <File className="h-4 w-4 mr-2 text-blue-600" />
          Word
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Predictions</DropdownMenuLabel>
        <DropdownMenuItem 
          onClick={() => downloadFile('predictions', 'pdf')}
          disabled={downloading?.startsWith('predictions')}
          className="cursor-pointer"
        >
          <FileText className="h-4 w-4 mr-2 text-red-600" />
          PDF
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => downloadFile('predictions', 'word')}
          disabled={downloading?.startsWith('predictions')}
          className="cursor-pointer"
        >
          <File className="h-4 w-4 mr-2 text-blue-600" />
          Word
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Risk Intelligence</DropdownMenuLabel>
        <DropdownMenuItem 
          onClick={() => downloadFile('risk_intelligence', 'pdf')}
          disabled={downloading?.startsWith('risk_intelligence')}
          className="cursor-pointer"
        >
          <FileText className="h-4 w-4 mr-2 text-red-600" />
          PDF
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => downloadFile('risk_intelligence', 'word')}
          disabled={downloading?.startsWith('risk_intelligence')}
          className="cursor-pointer"
        >
          <File className="h-4 w-4 mr-2 text-blue-600" />
          Word
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default AIExportDropdown;
